import dayjs from "dayjs";
import { Link } from "react-router-dom";
import "dayjs/locale/fr";
import { useTranslation } from "react-i18next";

import Status from "common/components/Status/Status";
import Button from "common/components/Button/Button";
import truncateString from "common/utils/truncateString";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import { Content } from "types/Content";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import Icon from "components/Icon/Icon";
import getFlattenText from "common/utils/getFlattenText";
import useAuth from "common/hooks/useAuth";
import formatPrice from "utils/formatPrice";
import ProfileBadges from "components/ProfileBadges/ProfileBadges";

const containerStyle =
  "flex px-4 py-4 mb-2 w-full relative bg-white cursor-pointer rounded-lg border border-slate-150 transition-all overflow-hidden";

function ContentItem({ item, onDelete, onNavigate }: any) {
  const {
    id,
    title,
    authorProfile,
    description,
    price,
    images,
    status,
    address,
    category,
    onlyForPro,
    updatedAt,
  } = item as Content;

  const { t } = useTranslation();
  const { user } = useAuth();

  const detailLink = `/contents/${id}`;

  return (
    <div className={`${containerStyle}  items-start lg:items-stretch flex-col lg:flex-row`}>
      <Link to={detailLink} className="w-full">
        <div className="responsive-flex-lg mb-4 lg:mb-0 w-full gap-2 lg:gap-4 pr:0 lg:pr-4">
          <div className="flex flex-col flex-1 justify-start overflow-hidden">
            {/* head */}
            <div className="flex flex-row w-full gap-4 flex-1 justify-between">
              <div className="flex flex-col flex-1">
                <h4 className="text-xl font-bold text-primary leading-6">{title}</h4>
              </div>
              <h4 className="text-md text-slate-500 leading-6 flex flex-row justify-start items-center font-normal text-sm">
                <Icon name="ClockIcon" className="w-4 h-4 mr-1" />
                {dayjs(updatedAt).fromNow()}
              </h4>
            </div>

            <div className="responsive-flex-lg flex-row justify-between mt-1">
              <div className="text-sm flex basis-1/2">
                <div className="flex items-center mr-4">
                  <span className="font-bold text-lg text-secondary">{formatPrice(price)}</span>
                </div>
              </div>
              <h4 className="text-md text-slate-500 leading-6 flex flex-row justify-start items-center font-normal text-sm">
                {/* <div className="w-8 h-8 rounded-md overflow-hidden mr-2">
                  <ImageComponent
                    image={ImageFromStrapiMedia(category.logo)?.uri}
                  />
                </div> */}
                <div className="border bg-stone-200 border-stone-200 text-stone-800 rounded-full py-0 px-2">
                  {category?.name}
                </div>
              </h4>
            </div>

            <div className="responsive-flex-lg flex-row mt-1">
              <div className="flex flex-row basis-1/2">
                <h4 className="font-bold text-md text-slate-500 leading-6 flex flex-row items-center">
                  <Icon name="UserIcon" className="w-4 h-4 mr-1" />
                  {authorProfile?.username}
                </h4>
                <ProfileBadges profile={authorProfile} classname="px-2" isSmall />
              </div>
              <div className="flex flex-row justify-end basis-1/2 gap-4 font-normal">
                {address && (
                  <h4 className="text-md text-slate-500 leading-6 flex flex-row flex-1 items-center justify-end">
                    <Icon name="MapPinIcon" className="w-4 h-4 mr-1" />
                    {`${address?.CP} ${address?.localisation}`}
                  </h4>
                )}
              </div>
            </div>

            <div className="responsive-flex-lg mt-1" />

            {/* foot */}
            <div className="flex flex-col 2xl:flex-row gap-2 pt-3 border-t border-slate-200">
              <div className="flex flex-row gap-4 relative shrink-0">
                {images?.map((image: any) => (
                  <div
                    key={image.id}
                    className="w-full h-full lg:w-16 lg:h-16 xl:w-24 xl:h-24 rounded-lg overflow-hidden"
                  >
                    <ImageComponent image={ImageFromStrapiMedia(image)?.uri} />
                  </div>
                ))}
              </div>
              {description && (
                <div className="text-sm text-gray-500 pt-2 flex-1 h-full pl-3 ml-2 border-l border-slate-200">
                  {truncateString(getFlattenText(description), 150)}
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>

      {/* right */}
      <div className="flex flex-col justify-between text-sm w-full lg:border-l lg:pl-4 lg:w-[30em] lg:border-slate-150 gap-2">
        <div className="flex w-full justify-between text-slate-600">
          {status ? <Status status={status} /> : <div />}

          <div className="flex items-center gap-3">
            {onlyForPro ? <div className="rounded-md border px-1">Pro uniquement</div> : null}
          </div>
        </div>

        {user?.isSuperAdmin && (
          <div className="flex w-full gap-1 justify-end">
            <Button
              type="error-line"
              icon="TrashIcon"
              compact
              onClick={() => onDelete(item?.id)}
              confirmMessage={t('actions.confirmDelete')}
            />

            {user?.isJuloaAdmin && (
              <Button
                title={t('actions.edit')}
                type="warning-line"
                icon="PencilSquareIcon"
                onClick={() => onNavigate(item?.id)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContentItem;
